import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { ReservationEventPayloadStandard } from '@r6digital/rapidstor-types/types/Events/Reservation';
import { ShowPricesPayload } from '@r6digital/rapidstor-types/types/Events/ShowPrices';
import {
    BaseReservation,
    RapidStorReservationRequest,
} from '@r6digital/rapidstor-types/types/Reservation';
import IconMailSent from '@/assets/icon-mail-sent.svg?react';
import IconCalendar from '@/assets/icon-calendar.svg?react';
import {
    usePatchReservationMutation,
    usePostReservationMutation,
    usePostSaveQuoteMutation,
} from '@/core/api/rapidstor';
import {
    formatCurrency,
    formatPriceLabel,
    monthlyToWeekly,
} from '@/core/utils/currency';
import { getEffectiveDealRate } from '@/journey-based-listing/components/UnitSelect/utils/dealMatching';
import {
    EMAIL_PILL_DOMAINS,
    applyEmailDomain,
    isValidTld,
    shouldShowEmailPills,
} from '@/core/utils/email';
import { isSafeUrl } from '@/core/utils/url';
import useBodyScrollLock from '@/core/hooks/useBodyScrollLock';
import { useExternalData } from '@/core/hooks/useExternalData';
import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';
import {
    descriptorDimensions,
    getDatePickerRange,
    getDefaultNeededDate,
    isFreeReservation,
    payLaterButtonText,
    payNowButtonText,
    saveQuoteButtonText,
    showPricesButtonText,
    toNeededDate,
} from '@/core/utils/rapidstor';
import { resolveModalCtas } from '@/standard-unit-listing/ctas';
import { getDealCardCopy, resolveSelectedDealId } from '@/standard-unit-listing/deals';
import { UnitTypeWithImages } from '@/standard-unit-listing/screens';

import Logger from '@/core/utils/logging';

import store from '@/store';
import {
    selectContactData,
    selectDetailsModal,
    selectReservationData,
    selectSelectedDeal,
    setContactData,
    setDetailsModal,
} from '@/store/bookingSlice';

import {
    reservationEvent,
    reservationViaMoveInEvent,
    showPricesEvent,
} from '@/features/rapidstor/PostMessageEvents';
import { unitTypeTitle } from '@/features/rapidstor/helpers';

import classes from './styles.module.scss';

import Button from '../elements/button';
import UnitTypeMeta from '../shared/UnitTypeMeta';
import PriceInfo from '../unit-type/PriceInfo';

type FormStruct = {
    location: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    neededDate: Date;
};

export default function DetailsModal() {
    const { isOpen, type, unitType } = useSelector(selectDetailsModal);
    const { t } = useTranslation();
    const location = useLoaderData() as Location;
    const ctas = resolveModalCtas(location, type);
    const [cookies, setCookie] = useCookies(['contactData', 'sessionId']);
    const contactData = useSelector(selectContactData);
    const reservationData = useSelector(selectReservationData);
    const selectedDeal = useSelector(selectSelectedDeal);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    type SubmissionType = 'payNow' | 'payLater' | 'saveQuote' | 'showPrices';
    const submissionTypeRef = useRef<SubmissionType>('payNow');
    const [makeReservation] = usePostReservationMutation();
    const [patchReservation] = usePatchReservationMutation();
    const [emailQuote] = usePostSaveQuoteMutation();
    const { trackEvent, trackSizesViewed } = useRapidStorTracking();
    const { generateExternalData } = useExternalData();
    const navigate = useNavigate();

    // Resolve the deal to book for a unit type. The user's pick lives on
    // `selectedDeal` when the listing has not created a reservation yet (the
    // usual case here), and on `reservationData` once one exists.
    const resolveDealId = (unitTypeToResolve: UnitTypeWithImages): string | null => (
        resolveSelectedDealId(
            unitTypeToResolve.deals,
            reservationData?.dealObject?._id
                ?? reservationData?.deal
                ?? selectedDeal.dealObject?._id
                ?? selectedDeal.deal,
        )
    );

    const nowDate = useMemo(() => moment(contactData?.neededDate || cookies?.contactData?.neededDate || new Date()), [contactData, cookies]);

    const [form, updateForm] = useImmer<FormStruct>({
        location: location.locationCode,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        neededDate: getDefaultNeededDate(location, nowDate),
    });

    const [errors, setErrors] = useImmer<Record<string, string | null>>({
        firstName: null,
        lastName: null,
        email: null,
        phone: null,
        neededDate: null,
    });

    const [emailTldError, setEmailTldError] = useState(false);

    useEffect(() => {
        if (cookies.contactData) {
            updateForm((draft) => {
                draft.firstName = cookies.contactData.firstName;
                draft.lastName = cookies.contactData.lastName;
                draft.email = cookies.contactData.email;
                draft.phone = cookies.contactData.phone;
                draft.neededDate = cookies.contactData.neededDate
                    ? new Date(cookies.contactData.neededDate)
                    : getDefaultNeededDate(location, nowDate);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useBodyScrollLock(isOpen);

    const closeContactCard = (skipEvent: boolean) => {
        if (!skipEvent) {
            trackEvent('modal_closed', {
                unitType: unitType ? unitType.typeName : 'N/A',
                unitSize: unitType
                    ? descriptorDimensions(unitType, location, { spaceAroundSeparator: false })
                    : 'N/A',
            });
        }

        setIsLoading(false);
        setIsSubmitted(false);
        setSubmitError(null);

        store.dispatch(
            setDetailsModal({
                isOpen: false,
                unitType: null,
                type: null,
            }),
        );
    };

    const dateRange = useMemo(
        () => getDatePickerRange(location, 'movein'),
        [location],
    );

    const showPills = useMemo(
        () => shouldShowEmailPills(form.email),
        [form.email],
    );

    const handleEmailChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            updateForm((draft) => {
                draft.email = val;
            });
            setErrors((draft) => {
                draft.email = null;
            });
            if (val.includes('@') && val.split('@')[1]?.includes('.')) {
                setEmailTldError(!isValidTld(val));
            } else {
                setEmailTldError(false);
            }
        },
        [updateForm, setErrors],
    );

    const handlePillClick = useCallback(
        (domain: string) => {
            const fullEmail = applyEmailDomain(form.email, domain);
            updateForm((draft) => {
                draft.email = fullEmail;
            });
            setErrors((draft) => {
                draft.email = null;
            });
            setEmailTldError(!isValidTld(fullEmail));
        },
        [form.email, updateForm, setErrors],
    );

    const headerLabel = useMemo(() => {
        switch (type) {
            case 'bookNow':
                return t(
                    'standardUnitListing:detailsModal.bookNowHeader',
                    'Book this Unit',
                );
            case 'showPrices':
                return showPricesButtonText(location);
            case 'contactMe':
                return t(
                    'standardUnitListing:detailsModal.sendEnquiryHeader',
                    'Send Enquiry',
                );
            default:
                return '';
        }
    }, [type, t, location]);

    // headerLabel is computed in the memo above (a function boundary): only the
    // showPrices branch is a RapidStor setting. Declare its source so the runtime
    // marker flags the header on that branch.
    const headerLabelSource = type === 'showPrices' ? 'showPricesButtonText' : undefined;

    const handleReservationSubmit = async (
        unitTypeToSubmit: UnitTypeWithImages,
        {
            showPrices = false,
            enquiry = false,
            reserveButton,
        }: {
            showPrices?: boolean;
            enquiry?: boolean;
            reserveButton?: 'PayNow' | 'PayLater';
        } = {},
    ) => {
        if (reservationData?.token) {
            return patchReservation({
                token: reservationData.token,
                locationCode: location.locationCode,
                corpCode: location.corpCode,
                payload: {
                    tenant: {
                        firstName: form.firstName,
                        lastName: form.lastName,
                        email: form.email,
                        phone: form.phone,
                    },
                    reservation: {
                        neededDate: toNeededDate(form.neededDate),
                        enquiry: true,
                        deal: resolveDealId(unitTypeToSubmit),
                        unitType: unitTypeToSubmit._id,
                    },
                    cart: {},
                    charges: {},
                },
            }).unwrap();
        }
        const isEnquiry = showPrices
            ? true
            : enquiry;
        const sendWebhookOnly = showPrices
            ? !location.rapidstor.showPricesBehaviour.sendEmail
                && !location.rapidstor.showPricesBehaviour.createLead
            : false;
        const isSendEmail = showPrices
            ? location.rapidstor.showPricesBehaviour.sendEmail
            : true;
        // A paid reservation requires the fee to be collected before the email is sent.
        // Signal to the API to suppress the initial email — it will be sent after add-fee succeeds.
        const pendingPayment = isEnquiry && !showPrices && !isFreeReservation(location, form.neededDate);
        const dealId = resolveDealId(unitTypeToSubmit);
        const externalData = generateExternalData();

        return makeReservation({
            locationCode: location.locationCode,
            payload: {
                customListing: true,
                deal: dealId,
                sourceUrl: window.location.href,
                enquiry: isEnquiry,
                pendingPayment,
                // Tells the API which journey the customer is on. Without it a reserve-only
                // booking looks like an abandoned move-in and, on sites that schedule
                // follow-up emails, its reservation email is held back instead of sent.
                ...(reserveButton && { selectedReserveButton: reserveButton }),
                neededDate: toNeededDate(form.neededDate),
                timezoneOffset: location.timezoneOffset * -60,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                insuranceCoverageId:
            reservationData?.descriptor.defaultInsuranceCoverage,
                tenant: {
                    tenantId: null,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                },
                unitType: unitTypeToSubmit._id,
                webhookOnly: sendWebhookOnly,
                ...(externalData && { externalData }),
                googleClickId: externalData?.gclid || undefined,
                sendEmail: isSendEmail,
            },
        }).unwrap();
    };

    const convertReservationToEventPayload = (
        reservation: BaseReservation,
    ): ReservationEventPayloadStandard => {
        const { unitType: reservationUnitType } = reservation;

        return {
            deal_name: unitType?.deal?.title || null,
            quoted_rate_monthly: reservation.rates.rate,
            quoted_rate_weekly: monthlyToWeekly(reservation.rates.rate),
            reservation,
            site_info: {
                location_code: location.locationCode,
                site_token: location.token,
            },
            size: reservationUnitType.area,
            tenant: {
                tenantId: reservation.tenantId,
                firstName: reservation.firstName,
                lastName: reservation.lastName,
                email: reservation.email,
                phone: reservation.phone,
            },
            transaction_id: reservation.token,
            unit_id: reservationUnitType.unitToUse?.UnitID ?? reservation.UnitID,
            unit_length: reservationUnitType.length,
            unit_name: reservation.sUnitName,
            unit_rate: reservation.rates.rate,
            unit_width: reservationUnitType.width,
            value: reservation.rates.rate,
        };
    };

    const onFormSubmit = async (
        formSubmissionType: 'payNow' | 'payLater' | 'saveQuote' | 'showPrices',
    ) => {
        if (isLoading || !unitType || !location) return;
        if (emailTldError || !isValidTld(form.email)) {
            setEmailTldError(true);
            return;
        }
        setSubmitError(null);
        setIsLoading(true);
        const newErrors: Record<string, string | null> = {
            firstName: null,
            lastName: null,
            email: null,
            phone: null,
            neededDate: null,
        };

        const fieldsToValidate: Array<keyof FormStruct> = [
            'firstName',
            'lastName',
            'email',
            'phone',
        ];

        fieldsToValidate.forEach((field) => {
            if (!form[field] || form[field].toString().trim() === '') {
                newErrors[field] =
                    `${field.charAt(0).toUpperCase()}${field.slice(
                        1,
                    )} is required`;
            }
        });

        // Simple email regex validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (form.email && !emailRegex.test(form.email)) {
            newErrors.email = 'Invalid email address';
        } else if (form.email && !isValidTld(form.email)) {
            newErrors.email = 'Please enter a valid email domain';
        }

        // Simple phone number validation (digits only, length between 7 and 15)
        const phoneRegex = /^\d{7,15}$/;
        if (form.phone && !phoneRegex.test(form.phone)) {
            newErrors.phone = 'Invalid phone number';
        }

        if (
            (location.rapidstor.showPricesShowNeeded || type !== 'showPrices')
                && dateRange?.min
                && dateRange?.max
        ) {
            const neededDateDay = moment(form.neededDate).startOf('day');
            const minDay = moment(dateRange.min).startOf('day');
            const maxDay = moment(dateRange.max).startOf('day');

            if (neededDateDay.isBefore(minDay) || neededDateDay.isAfter(maxDay)) {
                newErrors.neededDate = `Needed date must be between ${minDay.format(
                    location.rapidstor.dateFormat.toUpperCase(),
                )} and ${maxDay.format(location.rapidstor.dateFormat.toUpperCase())}`;
            }
        }

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some((error) => error !== null);
        if (hasErrors) {
            setIsLoading(false);
            return;
        }

        trackSizesViewed();

        let result;

        try {
            switch (formSubmissionType) {
                case 'payNow':
                    result = await handleReservationSubmit(unitType, { reserveButton: 'PayNow' });

                    trackEvent('pay_now_clicked', {
                        unitType: unitType.typeName,
                        unitSize: descriptorDimensions(unitType, location, { spaceAroundSeparator: false }),
                    });

                    reservationViaMoveInEvent(convertReservationToEventPayload(result));

                    closeContactCard(true);

                    navigate(`/move-in/${result.token}`);
                    break;
                case 'payLater': {
                    const free = isFreeReservation(location, form.neededDate);
                    result = await handleReservationSubmit(unitType, {
                        enquiry: !free,
                        reserveButton: 'PayLater',
                    });

                    trackEvent('pay_later_clicked', {
                        unitType: unitType.typeName,
                        unitSize: descriptorDimensions(unitType, location, { spaceAroundSeparator: false }),
                    });

                    if (free) {
                        // No reservation fee applies — confirm in-place instead of
                        // forcing the user into the reservation-with-fee wizard.
                        setIsLoading(false);
                        setIsSubmitted(true);
                        reservationEvent(convertReservationToEventPayload(result));
                    } else {
                        closeContactCard(true);
                        navigate(`/reservation-with-fee/${result.token}`);
                    }
                    break;
                }
                case 'showPrices': {
                    const showPricesPayload: ShowPricesPayload = {
                        firstName: form.firstName,
                        lastName: form.lastName,
                        phone: form.phone,
                        email: form.email,
                        neededDate: location.rapidstor.showPricesShowNeeded
                            ? form.neededDate
                            : null,
                        locationCode: location.locationCode,
                        locationName: location.name,
                        description: `${unitType.width}x${unitType.length} ${unitType.typeName}`,
                        selected_unit_size: `${unitType.width}x${unitType.length} ${location.rapidstor.areaSizeSymbol}`,
                        weekly_price: monthlyToWeekly(
                            unitType.rates.discounted
                                ? unitType.rates.discountedRate
                                : unitType.rates.rate,
                        ),
                        discount: unitType.deal?.title || '',
                        unitType: {
                            dcLength: unitType.length,
                            dcPreferredRate: 0,
                            dcWidth: unitType.width,
                            hasAvailableUnits: unitType.totalAvailable > 0,
                            hasComputedAvailable: unitType.hasComputedAvailable,
                            iFloor: unitType.floor,
                            sLocationCode: unitType.locationCode,
                            sTypeName: unitType.typeName,
                            unitTypeId: unitType._id,
                        },
                    };
                    if (unitType.deal) {
                        showPricesPayload.deal = {
                            ...unitType.deal,
                            ConcessionID: unitType.deal.discountPlan?.ConcessionID ?? null,
                        };
                    }
                    showPricesEvent(showPricesPayload);

                    // Wait for the lead request to land before unlocking. Storing
                    // the contact details reveals the prices on the cards behind
                    // the modal, so doing it any earlier leaves the user looking
                    // at unlocked prices through a modal that is still loading.
                    try {
                        await handleReservationSubmit(unitType, { showPrices: true });
                    } catch (err) {
                        // The user has handed over their details, so they still
                        // get what they were promised when the lead cannot be
                        // created (e.g. the unit type has no availability, which
                        // the API rejects) — the unlock below is not skipped.
                        Logger.error(
                            `[DetailsModal] Error creating show prices lead: ${JSON.stringify(err)}`,
                            location,
                        );
                    }

                    store.dispatch(
                        setContactData({
                            firstName: form.firstName,
                            lastName: form.lastName,
                            phone: form.phone,
                            email: form.email,
                            neededDate: location.rapidstor.showPricesShowNeeded
                                ? form.neededDate.toISOString()
                                : undefined,
                        }),
                    );

                    setCookie(
                        'contactData',
                        {
                            ...form,
                            neededDate: location.rapidstor.showPricesShowNeeded
                                ? form.neededDate.toISOString()
                                : undefined,
                        },
                        {
                            path: window.location.pathname,
                            maxAge: 60 * 60 * 24,
                        },
                    );

                    closeContactCard(true);

                    trackEvent('email_user_quoted', {
                        unitType: unitType.typeName,
                        unitSize: descriptorDimensions(unitType, location, { spaceAroundSeparator: false }),
                    });

                    setIsLoading(false);
                    break;
                }
                case 'saveQuote': {
                    const body: RapidStorReservationRequest = {
                        locationCode: location.locationCode,
                        corpCode: location.corpCode,
                        payload: {
                            customListing: true,
                            deal: resolveDealId(unitType),
                            sourceUrl: window.location.href,
                            enquiry: true,
                            neededDate: toNeededDate(form.neededDate),
                            timezoneOffset: location.timezoneOffset * -60,
                            tenant: {
                                tenantId: null,
                                firstName: form.firstName,
                                lastName: form.lastName,
                                email: form.email,
                                phone: form.phone,
                            },
                            unitType: unitType._id,
                            webhookOnly: false,
                        },
                    };

                    const externalData = generateExternalData();

                    if (externalData) {
                        body.payload.externalData = externalData;
                    }

                    await emailQuote(body).unwrap();

                    trackEvent('save_quote_clicked', {
                        unitType: unitType.typeName,
                        unitSize: descriptorDimensions(unitType, location, { spaceAroundSeparator: false }),
                    });

                    setIsSubmitted(true);
                    break;
                }
                default:
                    break;
            }
        } catch (err) {
            // Any failed submission (most commonly the API rejecting a unit type
            // with no availability) must surface an error and release the form —
            // otherwise the modal stays disabled and appears frozen.
            Logger.error(
                `[DetailsModal] Error submitting ${formSubmissionType} form: ${JSON.stringify(err)}`,
                location,
            );
            setSubmitError(t('contact_card:error_text'));
            setIsLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onFormSubmit(submissionTypeRef.current);
    };

    if (!isOpen || !unitType || !location) {
        return null;
    }

    // Summarise the price the user clicked through on: the deal selected in the
    // listing's price cards, falling back to the unit's promoted deal.
    const summaryDeal = unitType.deals?.find(
        (d) => d._id === resolveDealId(unitType),
    );
    const summaryCopy = summaryDeal ? getDealCardCopy(summaryDeal, location) : undefined;
    const standardRate = unitType.rates.rate;
    const effectiveRate = summaryDeal
        ? getEffectiveDealRate(summaryDeal, standardRate)
        : standardRate;
    const currencySymbol = location.rapidstor.currencySymbol ?? '';
    const pricePeriod = formatPriceLabel('', location, false, false).slice(
        currencySymbol.length,
    );

    return (
        <div
            className={classes.overlay}
            role="dialog"
            aria-modal="true"
            id="details-modal"
        >
            <div className={classes.card}>
                <button
                    type="button"
                    className={classes.closeButton}
                    aria-label={t('cta:close')}
                    onClick={() => closeContactCard(false)}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        strokeWidth={2}
                        className={classes.icon}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 6l12 12M6 18L18 6"
                        />
                    </svg>
                </button>
                <div className={classes.layout}>
                    <div className={classes.title} data-rs-setting-dynamic={headerLabelSource}>{headerLabel}</div>
                    <div className={classes.media}>
                        <div className={classes.mediaTop}>
                            <div className={classes.imageFrame}>
                                <img alt="Unit" className={classes.unitImage} src={unitType.picture || location.skin.defaultUnitPicture} />
                            </div>
                            <div className={classes.unitTypeDetails}>
                                <div className={classes.unitTypeName}>
                                    {unitTypeTitle(unitType, location)}
                                </div>
                                <UnitTypeMeta
                                    unitType={unitType}
                                    location={location}
                                    classes={classes}
                                />
                            </div>
                        </div>
                        {type !== 'showPrices' && (
                            <PriceInfo
                                summary
                                borderless={!summaryDeal}
                                discountLabel={summaryCopy?.label}
                                primaryLine={summaryCopy?.primary}
                                secondaryLine={summaryCopy?.secondary}
                                price={formatPriceLabel(
                                    formatCurrency(effectiveRate, location),
                                    location,
                                    false,
                                    true,
                                )}
                                period={pricePeriod}
                                originalPrice={effectiveRate < standardRate
                                    ? formatPriceLabel(
                                        formatCurrency(standardRate, location),
                                        location,
                                        false,
                                        false,
                                    )
                                    : undefined}
                            />
                        )}
                    </div>
                    <div className={classes.formArea}>
                        {isSubmitted ? (
                            <div className={classes.successMessage}>
                                <IconMailSent width={48} className={classes.icon} />
                                <span className={classes.text}>
                                    {submissionTypeRef.current === 'payLater'
                                            ? t('contact_card:reservation:success')
                                            : t('contact_card:quote:success')}
                                </span>
                                <Button
                                    size="medium"
                                    type="primary"
                                    onclick={() => closeContactCard(false)}
                                >
                                    {t('cta:close')}
                                </Button>
                            </div>
                            ) : (
                                <>
                                    <form
                                        className={classes.form}
                                        onSubmit={handleSubmit}
                                    >
                                        <div className={classes.block}>
                                            <label
                                                className={classes.label}
                                                htmlFor="rs-details-first-name"
                                            >
                                                {t('contact_card:first_name')}
                                                {' '}
                                                *
                                            </label>
                                            <input
                                                type="text"
                                                id="rs-details-first-name"
                                                name="firstName"
                                                autoComplete="given-name"
                                                placeholder=""
                                                className={classes.input}
                                                disabled={isLoading}
                                                value={form.firstName}
                                                onChange={(e) => {
                                                    updateForm((draft) => {
                                                        draft.firstName =
                                                            e.target.value;
                                                    });
                                                    setErrors((draft) => {
                                                        draft.firstName = null;
                                                    });
                                                }}
                                                required
                                            />
                                            {errors.firstName && (
                                                <span className={classes.error}>
                                                    {errors.firstName}
                                                </span>
                                            )}
                                        </div>
                                        <div className={classes.block}>
                                            <label
                                                className={classes.label}
                                                htmlFor="rs-details-last-name"
                                            >
                                                {t('contact_card:last_name')}
                                                {' '}
                                                *
                                            </label>
                                            <input
                                                type="text"
                                                id="rs-details-last-name"
                                                name="lastName"
                                                autoComplete="family-name"
                                                placeholder=""
                                                className={classes.input}
                                                disabled={isLoading}
                                                value={form.lastName}
                                                onChange={(e) => {
                                                    updateForm((draft) => {
                                                        draft.lastName =
                                                            e.target.value;
                                                    });
                                                    setErrors((draft) => {
                                                        draft.lastName = null;
                                                    });
                                                }}
                                                required
                                            />
                                            {errors.lastName && (
                                                <span className={classes.error}>
                                                    {errors.lastName}
                                                </span>
                                            )}
                                        </div>
                                        {location.rapidstor
                                            .showPricesShowNeeded ||
                                        type !== 'showPrices' ? (
                                            <div className={classes.block}>
                                                <label
                                                    className={classes.label}
                                                    htmlFor="rs-details-needed-date"
                                                >
                                                    {t(
                                                        'contact_card:move_in_date',
                                                    )}
                                                    {' '}
                                                    *
                                                </label>
                                                <DatePicker
                                                    id="rs-details-needed-date"
                                                    name="neededDate"
                                                    autoComplete="off"
                                                    showIcon
                                                    icon={<IconCalendar />}
                                                    toggleCalendarOnIconClick
                                                    selected={form.neededDate}
                                                    minDate={dateRange?.min}
                                                    maxDate={dateRange?.max}
                                                    required
                                                    onFocus={(e) => {
                                                        e.currentTarget.blur();
                                                    }}
                                                    excludeDates={location.rapidstor.datesClosed.split(',').map((d) => moment(d, 'DD-MM-YYYY').toDate())}
                                                    disabledKeyboardNavigation
                                                    dateFormat={
                                                        location.rapidstor
                                                            .dateFormat
                                                    }
                                                    onChange={(
                                                        e: Date | null,
                                                    ) => {
                                                        if (e) {
                                                            updateForm(
                                                                (draft) => {
                                                                    draft.neededDate =
                                                                        e;
                                                                },
                                                            );
                                                            setErrors(
                                                                (draft) => {
                                                                    draft.neededDate =
                                                                        null;
                                                                },
                                                            );
                                                        }
                                                    }}
                                                    className={`${classes.datepicker} ${classes.input}`}
                                                    disabled={isLoading}
                                                    ariaRequired="true"
                                                />
                                                {errors.neededDate && (
                                                    <span
                                                        className={
                                                            classes.error
                                                        }
                                                    >
                                                        {errors.neededDate}
                                                    </span>
                                                )}
                                            </div>
                                        ) : null}
                                        <div className={classes.block}>
                                            <label
                                                className={classes.label}
                                                htmlFor="rs-details-phone"
                                            >
                                                {t('contact_card:phone_number')}
                                                {' '}
                                                *
                                            </label>
                                            <input
                                                type="tel"
                                                id="rs-details-phone"
                                                name="phone"
                                                autoComplete="tel"
                                                placeholder=""
                                                className={classes.input}
                                                disabled={isLoading}
                                                value={form.phone}
                                                onChange={(e) => {
                                                    updateForm((draft) => {
                                                        draft.phone =
                                                            e.target.value;
                                                    });
                                                    setErrors((draft) => {
                                                        draft.phone = null;
                                                    });
                                                }}
                                                required
                                            />
                                            {errors.phone && (
                                                <span className={classes.error}>
                                                    {errors.phone}
                                                </span>
                                            )}
                                        </div>
                                        <div className={`${classes.block} ${classes.blockFullWidth}`}>
                                            <label
                                                className={classes.label}
                                                htmlFor="rs-details-email"
                                            >
                                                {t('contact_card:email_address')}
                                                {' '}
                                                *
                                            </label>
                                            <input
                                                type="email"
                                                id="rs-details-email"
                                                name="email"
                                                placeholder=""
                                                autoComplete="off"
                                                className={`${classes.input} ${emailTldError ? classes.inputError : ''}`}
                                                disabled={isLoading}
                                                value={form.email}
                                                onChange={handleEmailChange}
                                                required
                                            />
                                            {showPills && (
                                                <div
                                                    className={
                                                        classes.emailPills
                                                    }
                                                >
                                                    {EMAIL_PILL_DOMAINS.map(
                                                        (domain) => (
                                                            <button
                                                                key={domain}
                                                                type="button"
                                                                className={
                                                                    classes.emailPill
                                                                }
                                                                onClick={() => handlePillClick(domain)}
                                                            >
                                                                @
                                                                {domain}
                                                            </button>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                            {emailTldError && (
                                                <span className={classes.error}>
                                                    {t('contact_card:invalid_email_domain')}
                                                </span>
                                            )}
                                            {errors.email && !emailTldError && (
                                                <span className={classes.error}>
                                                    {errors.email}
                                                </span>
                                            )}
                                        </div>

                                        {submitError && (
                                            <div
                                                className={classes.submitError}
                                                role="alert"
                                            >
                                                {submitError}
                                            </div>
                                        )}

                                        <div className={classes.buttonGroup}>
                                            {ctas.showPrices ? (
                                                <Button
                                                    type="primary"
                                                    size="medium"
                                                    htmlType="submit"
                                                    onclick={() => {
                                                        submissionTypeRef.current =
                                                            'showPrices';
                                                    }}
                                                    disabled={isLoading}
                                                >
                                                    {showPricesButtonText(
                                                        location,
                                                    )}
                                                </Button>
                                            ) : (
                                                <>
                                                    {ctas.moveIn && (
                                                        <Button
                                                            type="primary"
                                                            size="medium"
                                                            htmlType="submit"
                                                            onclick={() => {
                                                                submissionTypeRef.current =
                                                                    'payNow';
                                                            }}
                                                            disabled={isLoading}
                                                            disableScroll
                                                        >
                                                            {payNowButtonText(
                                                                location,
                                                            )}
                                                        </Button>
                                                    )}
                                                    {ctas.reservation && (
                                                        <Button
                                                            type="outlinePrimaryOnWhite"
                                                            size="medium"
                                                            htmlType="submit"
                                                            onclick={() => {
                                                                submissionTypeRef.current =
                                                                    'payLater';
                                                            }}
                                                            disabled={
                                                                isLoading
                                                            }
                                                            disableScroll
                                                        >
                                                            {payLaterButtonText(
                                                                location,
                                                            )}
                                                        </Button>
                                                    )}
                                                    {ctas.saveQuote && (
                                                        <Button
                                                            type="black"
                                                            size="medium"
                                                            htmlType="submit"
                                                            onclick={() => {
                                                                submissionTypeRef.current =
                                                                    'saveQuote';
                                                            }}
                                                            disabled={isLoading}
                                                            disableScroll
                                                        >
                                                            {saveQuoteButtonText(
                                                                location,
                                                            )}
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </form>

                                    {(isSafeUrl(location.rapidstor.termsAndConditionsUrl) || isSafeUrl(location.rapidstor.privacyPolicyUrl)) && (
                                        <div className={classes.policy_links}>
                                            {isSafeUrl(location.rapidstor.termsAndConditionsUrl) && (
                                                <a
                                                    href={location.rapidstor.termsAndConditionsUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={classes.policy_link}
                                                >
                                                    {t('contact_card:terms_and_conditions')}
                                                </a>
                                            )}
                                            {isSafeUrl(location.rapidstor.privacyPolicyUrl) && (
                                                <a
                                                    href={location.rapidstor.privacyPolicyUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={classes.policy_link}
                                                >
                                                    {t('contact_card:privacy_policy')}
                                                </a>
                                            )}
                                        </div>
                                    )}

                                    <div className="details_modal_footer_text" />
                                </>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
}
